import * as React from 'react'
import { get } from 'lodash'
import Separator from '@doordash/component-separator'
import {
  Text,
  Colors,
  InlineChildren,
  Inset,
  Stack,
  Icon,
  Spacing,
} from '@doordash/design-language'
import { formatLateTime, formatTime } from '../utilities'
import { AlertIconContainer } from './styles'
import { DeliveryIssueData, ExtremelyLateData } from 'src/models/types'
import DateStoreNameHeading, {
  DateStoreHeadingType,
} from '../components/DateStoreHeading'
import ListCell from '@doordash/component-list-cell'
import t from './strings'

type ExtremelyLateListCellTemplateType = {
  showAlertIcon: boolean
  text: string
  time: string
}
const ExtremelyLateListCellTemplate: React.FC<ExtremelyLateListCellTemplateType> = ({
  showAlertIcon,
  text,
  time,
}) => (
  <ListCell
    renderBeforeContent={
      showAlertIcon
        ? () => (
            <AlertIconContainer>
              <Icon size={Icon.Sizes.Medium} type={Icon.Types.ErrorFill} />
            </AlertIconContainer>
          )
        : undefined
    }
    iconAlignment={ListCell.Alignment.Center}
    insetSize={ListCell.InsetSizes.None}
    isSeparatorVisible={false}
    subtext={<Text styles={Text.Styles.PageSubtext}>{text}</Text>}
    renderAfterContent={() => (
      <Text styles={Text.Styles.PageSubtext}>{formatTime(time)}</Text>
    )}
  />
)

type LateCardTypesProp = {
  storeHeading: DateStoreHeadingType
  showStoreHeading: boolean
  title: string
  scheduledTimeText: string
  actualTimeText: string
  issueData: ExtremelyLateData
}

export const LateCard: React.FC<LateCardTypesProp> = ({
  storeHeading,
  showStoreHeading,
  title,
  scheduledTimeText,
  actualTimeText,
  issueData,
}) => (
  <Inset size={Inset.Sizes.Small} isSquished={true}>
    <Stack size={Stack.Sizes.Small}>
      {showStoreHeading && <DateStoreNameHeading {...storeHeading} />}
      <InlineChildren size={InlineChildren.Sizes.XxxSmall}>
        <Text styles={Text.Styles.PageTitle}>{title}</Text>
        <Text styles={Text.Styles.PageTitle} color={Colors.SystemRed80}>
          {formatLateTime(issueData.minutesLate)}
        </Text>
      </InlineChildren>
    </Stack>
    <ExtremelyLateListCellTemplate
      showAlertIcon={false}
      text={scheduledTimeText}
      time={issueData.scheduledTime}
    />
    <Separator
      verticalMargin={Stack.Sizes.Small}
      type={Separator.Types.FullBorder}
    />
    <ExtremelyLateListCellTemplate
      showAlertIcon={true}
      text={actualTimeText}
      time={issueData.actualTime}
    />
  </Inset>
)

type DeliveryIssueLateCardProps = {
  storeHeading: DateStoreHeadingType
  issueData: DeliveryIssueData
}

export const DeliveryIssueLateCard: React.FC<DeliveryIssueLateCardProps> = ({
  storeHeading,
  issueData,
}) => {
  const pickUp = get(issueData, 'pickUp')
  const dropOff = get(issueData, 'dropOff')

  return (
    <Stack size={Stack.Sizes.Small}>
      {pickUp && (
        <LateCard
          storeHeading={storeHeading}
          title={t.lateCardStoreTitle()}
          scheduledTimeText={t.lateCardStoreScheduledDescription()}
          actualTimeText={t.lateCardStoreActualDescription()}
          issueData={pickUp}
          showStoreHeading
        />
      )}
      {pickUp && dropOff && (
        <Separator
          verticalMargin={Spacing.Small}
          separatorHeight={Spacing.XxxxSmall}
        />
      )}
      {dropOff && (
        <LateCard
          storeHeading={storeHeading}
          title={t.lateCardCustomerTitle()}
          scheduledTimeText={t.lateCardCustomerScheduledDescription()}
          actualTimeText={t.lateCardCustomerActualDescription()}
          issueData={dropOff}
          showStoreHeading={!pickUp}
        />
      )}
    </Stack>
  )
}

export default DeliveryIssueLateCard
